import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { X, Check, XCircle, Users } from 'lucide-react';
import { useDialogA11y } from '../ui/useDialogA11y';

interface Module {
  id: string;
  name: string;
  order_index: number;
  end_date: string;
}

interface Student {
  id: string;
  full_name: string;
}

interface AttendanceRecord {
  module_id: string;
  alumno_id: string;
  present: boolean;
}

interface AttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  courseName: string;
}

export function AttendanceModal({ isOpen, onClose, courseId, courseName }: AttendanceModalProps) {
  const [modules, setModules] = useState<Module[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  const hasModuleEnded = (endDateString: string): boolean => {
    if (!endDateString) return false;

    const endDate = new Date(endDateString);
    const now = new Date();

    return now.getTime() > endDate.getTime();
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, courseId]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadModules(), loadStudents(), loadAttendance()]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async () => {
    const { data, error } = await supabase
      .from('modules')
      .select('id, name, order_index, end_date')
      .eq('course_id', courseId)
      .order('order_index', { ascending: true });

    if (error) throw error;
    setModules(data || []);
  };

  const loadStudents = async () => {
    const { data, error } = await supabase
      .from('course_enrollments')
      .select(`
        profiles:alumno_id (
          id,
          full_name
        )
      `)
      .eq('course_id', courseId)
      .eq('status', 'active');

    if (error) throw error;
    const studentsList = data?.map((enrollment: any) => enrollment.profiles).filter(Boolean) || [];
    setStudents(studentsList);
  };

  const loadAttendance = async () => {
    const { data, error } = await supabase
      .from('attendance')
      .select('module_id, alumno_id, present')
      .in('module_id', (await supabase
        .from('modules')
        .select('id')
        .eq('course_id', courseId)
      ).data?.map(m => m.id) || []);

    if (error) throw error;
    setAttendanceData(data || []);
  };

  const getAttendance = (moduleId: string, studentId: string, moduleEndDate: string): boolean | null => {
    const record = attendanceData.find(
      (att) => att.module_id === moduleId && att.alumno_id === studentId
    );

    if (record !== undefined) {
      return record.present;
    }

    if (hasModuleEnded(moduleEndDate)) {
      return false;
    }

    return null;
  };

  const calculateAttendancePercentage = (studentId: string): number => {
    if (modules.length === 0) return 0;

    let attendedCount = 0;
    let totalClassesPassed = 0;

    modules.forEach((module) => {
      if (hasModuleEnded(module.end_date)) {
        totalClassesPassed++;
        const record = attendanceData.find(
          (att) => att.module_id === module.id && att.alumno_id === studentId
        );
        if (record && record.present === true) {
          attendedCount++;
        }
      }
    });

    if (totalClassesPassed === 0) return 0;
    return Math.round((attendedCount / totalClassesPassed) * 100);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-attendance"
        tabIndex={-1}
        className="glass-surface rounded-xl max-w-7xl w-full max-h-[90vh] border border-white/[0.06] flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <div>
            <h2 id="modal-title-attendance" className="text-xl font-bold text-white">Asistencia de Alumnos</h2>
            <p className="text-sm text-muted mt-1">{courseName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/[0.04] rounded-lg transition-all"
          >
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cubus-lime"></div>
          </div>
        ) : students.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12">
            <Users className="w-16 h-16 text-faint mb-4" />
            <p className="text-muted text-center">No hay alumnos inscritos en este curso</p>
          </div>
        ) : (
          <div className="flex-1 overflow-auto p-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-white/[0.08]">
                    <th className="text-left p-3 text-sm font-semibold text-white/70 glass-deep sticky left-0 z-10">
                      Alumno
                    </th>
                    {modules.map((module) => (
                      <th
                        key={module.id}
                        className="p-3 text-xs font-semibold text-white/70 glass-deep min-w-[80px]"
                      >
                        <div className="text-center">
                          <div className="mb-1">M{module.order_index}</div>
                          <div className="font-normal text-subtle truncate max-w-[100px]">
                            {module.name}
                          </div>
                        </div>
                      </th>
                    ))}
                    <th className="text-center p-3 text-sm font-semibold text-white/70 glass-deep min-w-[100px]">
                      Promedio
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => {
                    const percentage = calculateAttendancePercentage(student.id);
                    return (
                      <tr
                        key={student.id}
                        className="border-b border-white/[0.06] hover:bg-white/[0.04] transition-colors"
                      >
                        <td className="p-3 text-sm text-white font-medium glass-surface sticky left-0 z-10">
                          {student.full_name}
                        </td>
                        {modules.map((module) => {
                          const attended = getAttendance(module.id, student.id, module.end_date);
                          return (
                            <td key={module.id} className="p-3 text-center">
                              {attended === true ? (
                                <div className="flex items-center justify-center">
                                  <Check className="w-5 h-5 text-green-500" />
                                </div>
                              ) : attended === false ? (
                                <div className="flex items-center justify-center">
                                  <XCircle className="w-5 h-5 text-red-500" />
                                </div>
                              ) : (
                                <div className="flex items-center justify-center">
                                  <span className="text-faint">-</span>
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="p-3 text-center">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                              percentage >= 75
                                ? 'bg-green-500/20 text-green-400'
                                : percentage >= 50
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {percentage}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="p-6 border-t border-white/[0.06]">
          <div className="flex items-center gap-6 text-sm text-muted">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              <span>Presente</span>
            </div>
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" />
              <span>Ausente</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-faint">-</span>
              <span>Sin registrar</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
